import { delayAsync, registerComponent } from "@eusoft/webapp-core";
import { forModel } from "@eusoft/webapp-jsx";
import { Content, ObjectEditor, arrayItemsSource, formatString, formatText, required, validateWhen } from "@eusoft/webapp-ui";
import { UserAccountType } from "../entities/Commands";
import { Authorize } from "../services/Authorize";
import { type ContextEntityFeature, type ContextEntityType, context } from "../services/Context";
import type { IContextInfo } from "../components/ContextView";
import type { Guid, IEntityView, IMerchantListView, ISaleCircuitListView, ISalePointListView } from "../entities/Commands";
import type { IContentInfo, IContentOptions, IResultContainer } from "@eusoft/webapp-ui";
import type { IItemsContainer } from "@eusoft/webapp-ui/abstraction/IItemsContainer";

export interface IContextSelectorArgs {

    type: ContextEntityType;

    feature?: ContextEntityFeature;
}


export class ContextSelectorPage extends Content<IContextSelectorArgs> implements IResultContainer<boolean>, IContextInfo {

    constructor() {
        super();

        this.init(ContextSelectorPage, {
            title: "select-context",
            style: ["panel"],
            features: [Authorize({ accountType: UserAccountType.Business })],
            actions: [{
                name: "select",
                text: "select",
                priority: "primary",
                executeAsync: () => this.selectAsync()
            }],
            body: forModel(this, m => <>
                <ObjectEditor<this>
                    commitMode="auto-inplace"
                    validationMode="onInputChange"
                    style="vertical"
                    ref={m.editor}
                    value={m}
                    inputField={{ style: "filled" }}
                    builder={bld => <>
                        {bld.singleSelector(a => a.saleCircuitId, {
                            label: "sale-circuit",                            
                            onChanged: async () => {
                                await delayAsync(0);
                                (bld.container.getPropEditor("merchantId") as IItemsContainer).refreshAsync();
                                (bld.container.getPropEditor("salePointId") as IItemsContainer).refreshAsync();
                            },                              
                            editor: {
                                emptyItem: formatString("[select]"),
                                itemsSource: arrayItemsSource(() => this.saleCircuitList,
                                    i => i.name,
                                    i => i.id)
                            }
                        })}

                        {bld.singleSelector(a => a.merchantId, {
                            label: "merchant",
                            validators: [validateWhen(() => this.contextType == "merchant", required)],
                            onChanged: async () => {
                                await delayAsync(0);
                                (bld.container.getPropEditor("salePointId") as IItemsContainer).refreshAsync();
                            },
                            editor: {
                                emptyItem: this.contextType == "merchant" ? formatString("[select]") : " ",
                                itemsSource: arrayItemsSource(() => this.getMerchants(),
                                    i => i.name,
                                    i => i.id)
                            }
                        })}

                        {bld.singleSelector(a => a.salePointId, {
                            label: "sale-point",
                            validators: [validateWhen(() => this.contextType == "sale-point", required)],
                            editor: {
                                emptyItem: " ",
                                itemsSource: arrayItemsSource(() => this.getSalePoints(),
                                    i => i.name,
                                    i => i.id)
                            }
                        })}
                    </>}
                />
            </>),
        } as IContentOptions<object>);
    }


    override async onLoadAsync(args: IContextSelectorArgs) {

        await context.refreshAsync();

        this.contextType = args.type;
        this.contextFeature = args.feature;

        this.saleCircuitList = context.saleCircutList;

        this.saleCircuitId = context.saleCircuit?.id ?? context.saleCircutList[0]?.id;

        this.salePointId = context.salePoint?.id;

        this.merchantId = context.merchant?.id;

        this.loadFromIds();

        return true;
    }

    getSalePoints() {

        this.saleCircuit = this.saleCircuitList.find(a => a.id == this.saleCircuitId);

        return this.saleCircuit?.salePoints.filter(a => {

            if (this.merchantId && a.merchant?.id != this.merchantId)
                return false;

            if (this.contextType == "sale-point" && this.contextFeature !== undefined)
                return (a.type & this.contextFeature) != 0;

            return true;
        })
    }

    getMerchants() {

        this.saleCircuit = this.saleCircuitList.find(a => a.id == this.saleCircuitId);
        return this.saleCircuit?.merchants?.map(a=> a.merchant);
    }

    protected loadFromIds() {

        this.saleCircuit = this.saleCircuitList.find(a => a.id == this.saleCircuitId);

        if (this.saleCircuit) {
            this.salePoint = this.saleCircuit.salePoints?.find(a => a.id == this.salePointId);
            this.merchant = this.saleCircuit.merchants?.find(a => a.merchant?.id == this.merchantId)?.merchant;
        }
            
    }

    async selectAsync() {

        if (!await this.editor.validateAsync())
            return;

        this.loadFromIds();

        if (!this.salePoint && this.contextType == "sale-point")
            return;

        context.saleCircuit = this.saleCircuit;

        context.salePoint = this.salePoint;

        context.merchant = this.merchant;

        if (this.salePoint) {

            const merchant = this.saleCircuit.merchants.find(a => a.merchant?.id == this.salePoint.merchant.id);
            context.merchant = { ...merchant.merchant };
        }
        else if (this.saleCircuit) {
            if (this.saleCircuit.merchants?.length == 1)
                context.merchant = this.saleCircuit.merchants[0].merchant;

        }

        await context.updateOptionsAsync();

        context.save();

        await this.host.closeAsync(true);
    }

    editor: ObjectEditor<this>;

    contextType: ContextEntityType;

    contextFeature: ContextEntityFeature;

    saleCircuitId: Guid;

    salePointId: Guid;

    merchantId: Guid;

    saleCircuit: ISaleCircuitListView;

    salePoint: ISalePointListView;

    merchant: IEntityView;

    saleCircuitList: ISaleCircuitListView[];

    result = false;

    hideContext = true;

    static override info = {
        name: "context-selector",
        route: "/context",
        icon: "",
        factory: () => new ContextSelectorPage()
    } as IContentInfo;
}

registerComponent(ContextSelectorPage, "ContextSelectorPage");